/**
 * #1291: a retry of a dbt run reruns the jobs its first pass set out to run, or nothing.
 *
 * A failed pass names its jobs on the transform step (`metrics.plannedDbtJobIds`, #1119, else
 * `ranDbtJobIds`). A retry that ran only the jobs still enabled turned a run whose failing job
 * never succeeded into Succeeded, and Overview read "No open failures". So the web refuses the
 * retry (409 `DBT_JOB_DISABLED`, `server/services/RunService.ts` `applyRetry`) and the runner
 * fails a retried pass with the same sentence (`runner/steps/transform.ts` `loadDbtJobs`) when
 * one of those jobs is disabled or deleted, the way B-12 refuses a disabled connection.
 */

/** The step metrics keys that name a pass's dbt jobs, read in this order. */
export const RETRY_DBT_JOB_ID_KEYS = ["plannedDbtJobIds", "ranDbtJobIds"] as const;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The saved dbt jobs a transform step's pass set out to run: its `plannedDbtJobIds`, else its
 * `ranDbtJobIds`, as UUIDs only ("Chosen models" names no saved job). Empty when neither is
 * recorded, which is a pass that never knew its jobs.
 */
export function retryDbtJobIds(metrics: unknown): string[] {
  if (!metrics || typeof metrics !== "object") return [];
  const record = metrics as Record<string, unknown>;
  for (const key of RETRY_DBT_JOB_ID_KEYS) {
    const value = record[key];
    if (Array.isArray(value)) {
      return [
        ...new Set(value.filter((id): id is string => typeof id === "string" && UUID_RE.test(id))),
      ];
    }
  }
  return [];
}

/** "A", "A and B", "A, B and C". */
function nameList(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * Who reads the refusal, which decides its next step (#1084: a suggested action follows the
 * reader's permission). `enable`: the reader holds `dbt_jobs.write` and is told to enable the
 * job. `ask`: the reader cannot enable it (a Contributor) and is told to ask an instance admin.
 * `runner`: the step error a retried pass writes, read later by anyone, so it names no action
 * that needs a permission.
 */
export type DbtJobRetryAudience = "enable" | "ask" | "runner";

/**
 * The one sentence a refused retry says, or null when every job is still there and enabled.
 * A deleted job cannot be enabled again, so a deleted one says "start a new run" alone.
 */
export function dbtJobRetryRefusal(input: {
  disabled: readonly string[];
  deleted: number;
  audience: DbtJobRetryAudience;
}): string | null {
  if (input.deleted > 0) {
    return input.deleted === 1
      ? "A dbt job of this run was deleted, so this run cannot be retried. Start a new run."
      : "Some dbt jobs of this run were deleted, so this run cannot be retried. Start a new run.";
  }
  if (input.disabled.length === 0) return null;
  const many = input.disabled.length > 1;
  const subject = `${nameList(input.disabled)} ${many ? "are" : "is"} disabled`;
  const them = many ? "them" : "it";
  switch (input.audience) {
    case "enable":
      return `${subject}. Enable ${them} to retry this run, or start a new run.`;
    case "ask":
      return `${subject}. Ask an instance admin to enable ${them}, or start a new run.`;
    case "runner":
      return `${subject}, so this retry did not run its dbt jobs. Start a new run, or retry once ${many ? "they are" : "it is"} enabled again.`;
  }
}
